import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';

/**
 * Custom hook for MSAL authentication and token acquisition
 * Wraps the MSAL instance and provides convenient token acquisition methods
 */
const useAuth = () => {
  const { instance, accounts } = useMsal();

  /**
   * Check if user is authenticated (has at least one account)
   */
  const isAuthenticated = accounts && accounts.length > 0;

  /**
   * Get the current active account
   */
  const activeAccount = accounts?.[0] || null;

  /**
   * Acquires an access token silently using the configured scopes
   * @returns {Promise<string>} The access token
   * @throws {Error} If token acquisition fails or no account is available
   */
  const acquireToken = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error('No authenticated account available');
    }

    const tokenRequest = {
      ...loginRequest,
      account: accounts[0],
      scopes: [
        process.env.REACT_APP_AUTH_SCOPE,
        process.env.REACT_APP_AUTH_SCOPE_PROVIDER,
        process.env.REACT_APP_AUTH_SCOPE_GROUP,
      ],
    };

    const response = await instance.acquireTokenSilent(tokenRequest);
    return response.accessToken;
  }, [instance, accounts, isAuthenticated]);

  /**
   * Acquires token and returns both the token and decoded claims
   * @returns {Promise<{accessToken: string, account: object}>}
   */
  const acquireTokenWithAccount = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error('No authenticated account available');
    }

    const tokenRequest = {
      ...loginRequest,
      account: accounts[0],
      scopes: [
        process.env.REACT_APP_AUTH_SCOPE,
        process.env.REACT_APP_AUTH_SCOPE_PROVIDER,
        process.env.REACT_APP_AUTH_SCOPE_GROUP,
      ],
    };

    const response = await instance.acquireTokenSilent(tokenRequest);
    return {
      accessToken: response.accessToken,
      account: response.account,
    };
  }, [instance, accounts, isAuthenticated]);

  return {
    instance,
    accounts,
    activeAccount,
    isAuthenticated,
    acquireToken,
    acquireTokenWithAccount,
  };
};

export default useAuth;
